// =============================================================================
// Admin Analytics — WidgetContainer
// =============================================================================
// Titled card for the chart/table widgets. Same surface as the Video Analytics
// CardContainer, with the shared two-button toolbar, badges, and an optional
// subtitle.
// =============================================================================
import { clsx } from "clsx";
import { PropsWithChildren } from "react";

import { Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import { WidgetBadge, WidgetTimeOverride, WidgetToolbar } from "@pages/AdminAnalytics/components/WidgetChrome";

interface WidgetContainerProps extends PropsWithChildren {
  title: string;
  infoTooltip?: string;
  subtitle?: React.ReactNode;
  timeOverride: WidgetTimeOverride;
  onViewData?: () => void;
  badges?: Array<"catalight" | "beta" | "estimated">;
  className?: string;
  /** Extra content rendered inside the filter popover (e.g. a category picker). */
  filterSlot?: React.ReactNode;
  /** Lights up the dot when a popover filter is non-default (e.g. category changed). */
  extraActive?: boolean;
  /** Resets extra filters (e.g. category) when the reset button is clicked. */
  onResetExtra?: () => void;
  /** Additional icon buttons rendered to the left of the filter button (e.g. a view-mode toggle). */
  extraButtons?: React.ReactNode;
}

export const WidgetContainer: React.FC<WidgetContainerProps> = ({
  title,
  infoTooltip,
  subtitle,
  timeOverride,
  onViewData,
  badges,
  className,
  filterSlot,
  extraActive,
  onResetExtra,
  extraButtons,
  children,
}) => (
  <div className={clsx("flex flex-col gap-4 rounded-lg border border-limestone-200 bg-white p-6", className)}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Label size="small" displayType="loud" colorType="primary">
            {title}
          </Label>
          {infoTooltip ? <Tooltip infoIconClassName="w-4! h-4! shrink-0" text={infoTooltip} /> : null}
          {badges?.map((b) => <WidgetBadge key={b} kind={b} />)}
        </div>
        {subtitle ? (
          <Label size="extra-small" colorType="secondary">
            {subtitle}
          </Label>
        ) : null}
      </div>
      <WidgetToolbar
        timeOverride={timeOverride}
        onViewData={onViewData}
        filterSlot={filterSlot}
        extraActive={extraActive}
        onResetExtra={onResetExtra}
        extraButtons={extraButtons}
      />
    </div>
    {children}
  </div>
);
